import { createSelector } from 'reselect';
import { get, isEmpty } from 'lodash';
import moment from 'moment-timezone';
import { IState as IChartState } from './reducer';
import {
  SCOPE,
  CHART_SCOPE,
  ZOOM_CHART_REQUEST_SCOPE,
  BRUSH_CHART_REQUEST_SCOPE,
  BRUSH_COMPARE_CHART_REQUEST_SCOPE,
  SECONDARY_LINE_CHART_REQUEST_SCOPE,
  SECONDARY_COMPARE_LINE_CHART_REQUEST_SCOPE,
} from './types';
import commonSelector, {
  intervalSelector,
  realTimeModeOnTrendsSelector,
} from '@store/common/selectors';
import featureNames from '@utils/featureToggle/constants';
import { getCompareIntervalForDimensionTable } from '@src/components/common/GranularitySelect/utils';
import { comparisonIntervalSelector } from '@store/summaryContainer/comparison/selectors';
export interface IInterval {
  from: string | number;
  to: string | number;
}

const TIMESTAMP_DISPLAY_FORMAT = 'MMM DD HH:mm';

export const formatter = (ts: string | number) =>
  ts ? moment(ts).format(TIMESTAMP_DISPLAY_FORMAT) : '';

const selector = (state) => get(state, SCOPE);

export const chartSelector = (state): IChartState => get(state, CHART_SCOPE);
export const zoomChartSelector = (state) => get(state, ZOOM_CHART_REQUEST_SCOPE);
export const brushChartSelector = (state) => get(state, BRUSH_CHART_REQUEST_SCOPE);
export const brushCompareChartSelector = (state) => get(state, BRUSH_COMPARE_CHART_REQUEST_SCOPE);
export const secondaryLineChartSelector = (state) => get(state, SECONDARY_LINE_CHART_REQUEST_SCOPE);
export const secondaryCompareLineChartSelector = (state) =>
  get(state, SECONDARY_COMPARE_LINE_CHART_REQUEST_SCOPE);

export default selector;

export const defaultValueSelector = createSelector(chartSelector, (state: IChartState) => ({
  defaultZoom: state.defaultZoom,
  defaultBrush: state.defaultBrush,
}));

export const zoomSelector = createSelector(chartSelector, (state: IChartState) => state.zoom);

export const brushSelector = createSelector(chartSelector, (state: IChartState) => state.brush);

export const zoomIntervalSelector = createSelector(chartSelector, (state: IChartState) => ({
  from: state.interval[0],
  to: state.interval[1],
}));

export const brushIntervalSelector = createSelector(brushSelector, (brush: [number, number]) => ({
  from: brush[0],
  to: brush[1],
}));

export const noBrushSelector = createSelector(brushIntervalSelector, (brush) => {
  const { from, to } = brush;
  return from === 0 && to === 0;
});

export const chartTypeSelector = createSelector(
  chartSelector,
  commonSelector,
  (state: IChartState, commonStore) =>
    get(commonStore, ['features', featureNames.DISTRIBUTION_METRICS]) ? state.chartType : '',
);

export const engagementTypeSelector = createSelector(
  chartSelector,
  (state: IChartState) => state.engagementType,
);

export const bucketSelector = createSelector(chartSelector, (state: IChartState) => state.bucket);

export const chartIntervalSelector = createSelector(
  intervalSelector,
  zoomIntervalSelector,
  brushIntervalSelector,
  chartTypeSelector,
  realTimeModeOnTrendsSelector,
  (interval: IInterval, zoomInterval, brushInterval, chartType, realTimeMode) => {
    const { from, to, granularity } = interval;

    const { from: zoomFrom, to: zoomTo } = zoomInterval;
    const { from: brushFrom, to: brushTo } = brushInterval;

    if (brushFrom && brushTo) {
      return {
        from: brushFrom,
        to: brushTo,
        granularity,
      };
    }

    if (zoomFrom && zoomTo) {
      return {
        from: zoomFrom,
        to: zoomTo,
        granularity,
      };
    }

    return {
      from,
      to,
      granularity,
    };
  },
);

export const tableCompareIntervalSelector = createSelector(
  chartIntervalSelector,
  noBrushSelector,
  comparisonIntervalSelector,
  (interval, noBrush, comparisonInterval) => {
    const dodEnabled = !isEmpty(comparisonInterval);
    const isChartSliced = !noBrush;
    let tableCompareInterval: any = null;
    if (dodEnabled) {
      tableCompareInterval = comparisonInterval;
    }
    if (isChartSliced) {
      tableCompareInterval = getCompareIntervalForDimensionTable(interval);
    }
    return tableCompareInterval;
  },
);

export const tableCompareEnabledSelector = createSelector(
  chartSelector,
  noBrushSelector,
  tableCompareIntervalSelector,
  (state: IChartState, noBrush, tableCompareInterval) => {
    const isChartSliced = !noBrush;
    if (tableCompareInterval) {
      return state.tableCompareEnabled;
    }
    return false;
  },
);

export const chartIntervalTextSelector = createSelector(
  chartIntervalSelector,
  ({ from, to }: IInterval) => `${formatter(from)} - ${formatter(to)}`,
);

// Get the interval from bar chart
export const analysisIntervalSelector = createSelector(
  chartIntervalSelector,
  ({ from, to }: IInterval) => [formatter(from), formatter(to)],
);

export const birdChartSharedSelector = createSelector(chartSelector, (store) => ({
  ...store,
  defaultZoom: store.zoom,
  defaultBrush: store.brush,
}));
